import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1120polDueDates = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 1120-POL Due Date | Filing Deadline & Extension"
          description="Learn when Form 1120-POL is due, how to calculate fiscal-year deadlines, request an extension, and handle late filing or tax payments. "
          keywords=""
          canonical="resources/tax-exempt/form-1120-pol-filing-due-dates/"
          ogType="article"
          jsonLdPricing="89.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          Form{' '}
          <span className="highlight-container">
            <span className="highlight"> 1120-POL </span>
          </span>{' '}
          Due Date
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Stay ahead of the Form 1120-POL deadline with clear filing dates,
          extension options,
          <br /> and key deadline rules for your tax year.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1120polduedates`}
          >
            E-file Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/form-1120-pol-filing-due-dates/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  {/* <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box> */}

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 1120-POL at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>Purpose: </strong>{' '}
                        <Link
                          href="/tax-exempt/e-file-irs-form-1120-pol/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          IRS Form 1120-POL
                        </Link>{' '}
                        is used to report political organization taxable income
                        and calculate the federal income tax liability under{' '}
                        <Link
                          href="/tax-exempt/e-file-irs-form-1120-pol/#difference"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Section 527
                        </Link>
                        .
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>Who files:</strong>{' '}
                        Political organizations and certain tax-exempt
                        organizations subject to{' '}
                        <Link
                          href="/tax-exempt/e-file-irs-form-1120-pol/#difference"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Section 527(f)(1)
                        </Link>{' '}
                        may need to file Form 1120-POL.
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          Filing deadline: April 15
                        </strong>{' '}
                        for calendar-year organizations; fiscal-year filers are
                        due on the{' '}
                        <strong style={{ fontWeight: 600 }}>
                          15th day of the fourth month after tax year-end
                        </strong>
                        .{' '}
                      </span>
                    </li>
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Weekend or holiday:{' '}
                        </strong>{' '}
                        The deadline moves to the next business day when the
                        15th falls on a Saturday, Sunday, or legal holiday.
                      </span>
                    </li>
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>Extension:</strong>{' '}
                        Complete{' '}
                        <Link
                          href="/tax-extension/efile-irs-form-7004-business-tax-extension-online/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          IRS Form 7004
                        </Link>{' '}
                        before the actual filing deadline for an automatic
                        six-month extension to file.
                      </span>
                    </li>
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          Payment of Tax:
                        </strong>{' '}
                        Filing an extension is not an extension of the tax
                        payment deadline.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Box
                  sx={{
                    pt: 0,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whenis" sx={{ color: '#0202ff' }}>
                          When Is Form 1120-POL Due?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#canyou" sx={{ color: '#0202ff' }}>
                          Can You Extend the Form 1120-POL Filing Deadline?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whathappens" sx={{ color: '#0202ff' }}>
                          What Happens If You Miss the Form 1120-POL Deadline?
                        </Link>
                        <List sx={{ pb: 0 }}>
                          <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link
                                href="#form1120pol"
                                sx={{ color: '#0202ff' }}
                              >
                                Form 1120-POL Late Filing and Payment
                              </Link>
                            </ListItemText>
                          </ListItem>
                        </List>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#filing" sx={{ color: '#0202ff' }}>
                          2026 filing deadline
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#form1120polvs" sx={{ color: '#0202ff' }}>
                          Form 1120-POL vs. Form 990: Are the Due Dates the
                          Same?
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  id="whenis"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                >
                  When Is Form 1120-POL Due?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <Link
                    href="/resources/tax-exempt/form-1120-pol-instructions/"
                    sx={{
                      color: '#0202ff',
                      textDecoration: 'underline',
                    }}
                  >
                    Form 1120-POL
                  </Link>{' '}
                  <strong style={{ fontWeight: 600 }}>
                    is due on the 15th day of the 4th month after the political
                    organization’s tax year ends.
                  </strong>{' '}
                  For a calendar-year organization whose tax year ends December
                  31, the{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Form 1120-POL due date is April 15 of the following year
                  </strong>
                  .{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Organizations using a fiscal year should determine their
                  filing deadline based on the month their tax year ends. Use
                  the table below to find the corresponding{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Form 1120-POL due date.
                  </strong>{' '}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Form 1120-POL Due Date for Fiscal-Year Organizations
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For fiscal-year organizations, the{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Form 1120-POL due date is the 15th day of the fourth month
                    after the tax year ends
                  </strong>{' '}
                  and may fall in the following calendar year.
                </Typography>
                <Alert
                  severity="info"
                  icon={false}
                  sx={{
                    mb: 4,
                    border: '1px solid rgb(4, 156, 219),',
                    textAlign: 'center',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 2,
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>
                    Tax year-end → 4th month → 15th day = Form 1120-POL due
                    date{' '}
                  </strong>
                </Alert>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 600,
                              fontSize: 16,
                            }}
                          >
                            Tax Year Ends
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 600,
                              fontSize: 16,
                            }}
                          >
                            Form 1120-POL Due Date
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>
                            January 31
                          </TableCell>

                          <TableCell>May 15</TableCell>
                        </TableRow>

                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>
                            February 28/29
                          </TableCell>
                          <TableCell>June 15</TableCell>
                        </TableRow>

                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>March 31</TableCell>
                          <TableCell>July 15</TableCell>
                        </TableRow>

                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>April 30</TableCell>
                          <TableCell>August 15</TableCell>
                        </TableRow>

                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>May 31</TableCell>
                          <TableCell>September 15</TableCell>
                        </TableRow>

                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>June 30</TableCell>
                          <TableCell>October 15</TableCell>
                        </TableRow>

                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>July 31</TableCell>
                          <TableCell>November 15</TableCell>
                        </TableRow>

                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>August 31</TableCell>
                          <TableCell>December 15</TableCell>
                        </TableRow>

                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>
                            September 30
                          </TableCell>
                          <TableCell>January 15</TableCell>
                        </TableRow>

                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>
                            October 31
                          </TableCell>
                          <TableCell>February 15</TableCell>
                        </TableRow>

                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>
                            November 30
                          </TableCell>
                          <TableCell>March 15</TableCell>
                        </TableRow>

                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>
                            December 31
                          </TableCell>
                          <TableCell>April 15</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Alert
                  severity="info"
                  icon={<InfoIcon sx={{ color: '#640378' }} />}
                  sx={{
                    mb: 2,
                    mt: 0,
                    borderLeft: '3px solid #640378',
                    backgroundColor: '#f7e1ff',
                    color: '#444',
                  }}
                >
                  <strong>Important:</strong> If the Form 1120-POL due date
                  falls on a{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Saturday, Sunday, or legal holiday
                  </strong>
                  , the filing deadline automatically moves to the{' '}
                  <strong style={{ fontWeight: 600 }}>next business day</strong>
                  . Check the applicable IRS tax calendar for the exact date for
                  your filing year.
                </Alert>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          color: '#f8f9fa',
                          fontSize: 16,
                        }}
                      >
                        Don’t Miss the Form 1120-POL Deadline - E-File Today!
                      </Typography>
                      <Typography variant="body2" color="#f5efeb">
                        File your Form 1120-POL on time with TaxZerone and
                        simplify your filing with a fast, secure e-file
                        experience
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 200, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1120polduedates`}
                      >
                        E-File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                <Typography
                  variant="h2"
                  id="canyou"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                >
                  Can You Extend the Form 1120-POL Filing Deadline?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Organizations that require additional time to prepare{' '}
                  <Link
                    href="/resources/tax-exempt/form-1120-pol-instructions/"
                    sx={{
                      color: '#0202ff',
                      textDecoration: 'underline',
                    }}
                  >
                    1120-POL
                  </Link>{' '}
                  may apply for a six-month automatic extension by{' '}
                  <Link
                    href="/tax-extension/efile-irs-form-7004-business-tax-extension-online/ "
                    sx={{
                      color: '#0202ff',
                      textDecoration: 'underline',
                    }}
                  >
                    filing Form 7004
                  </Link>{' '}
                  on the original due date of their return. This extension gives
                  more time to file the form, but it does not extend the
                  deadline to pay any taxes owed.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 2,
                    lineHeight: 1.4,
                  }}
                >
                  Form 7004 Extension at a Glance
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 600,
                              fontSize: 16,
                            }}
                          >
                            Extension Detail
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 600,
                              fontSize: 16,
                            }}
                          >
                            Requirement
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>
                            <strong style={{ fontWeight: 600 }}>
                              Extension form
                            </strong>
                          </TableCell>
                          <TableCell>
                            <Link
                              href="/resources/tax-extension/form-7004-instructions/"
                              sx={{
                                color: '#0202ff',
                                textDecoration: 'underline',
                              }}
                            >
                              Form 7004
                            </Link>{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>
                            <strong style={{ fontWeight: 600 }}>
                              When to file
                            </strong>
                          </TableCell>
                          <TableCell>
                            On or before the original Form 1120-POL due
                            date{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>
                            <strong style={{ fontWeight: 600 }}>
                              Extension period
                            </strong>
                          </TableCell>
                          <TableCell>6 months </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>
                            <strong style={{ fontWeight: 600 }}>
                              What it extends
                            </strong>
                          </TableCell>
                          <TableCell>Time to file Form 1120-POL </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>
                            <strong style={{ fontWeight: 600 }}>
                              What it does not extend
                            </strong>
                          </TableCell>
                          <TableCell>Time to pay tax due </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Alert
                  severity="info"
                  icon={<InfoIcon sx={{ color: '#640378' }} />}
                  sx={{
                    mb: 2,
                    mt: 0,
                    borderLeft: '3px solid #640378',
                    backgroundColor: '#f7e1ff',
                    color: '#444',
                  }}
                >
                  Form 7004 extends the time to file, but not the time to pay
                  any tax due. Pay any tax owed by the original Form 1120-POL
                  due date to avoid or minimize interest and potential
                  penalties.
                </Alert>
                <Typography
                  variant="h2"
                  id="whathappens"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                >
                  What Happens If You Miss the Form 1120-POL Deadline?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If
                  <strong style={{ fontWeight: 600 }}>Form 1120-POL</strong> is
                  filed late, there will be{' '}
                  <strong style={{ fontWeight: 600 }}>penalties</strong>,
                  including{' '}
                  <strong style={{ fontWeight: 600 }}>interest charges</strong>{' '}
                  where tax is due. Filing an extension on time can provide
                  additional time to submit the return, but a late extension
                  request does not remove penalties that may already apply.
                </Typography>
                <Typography
                  variant="h3"
                  id="form1120pol"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 2,
                    lineHeight: 1.4,
                    scrollMarginTop: 83,
                  }}
                >
                  Form 1120-POL Late Filing and Payment
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 600,
                              fontSize: 16,
                            }}
                          >
                            Situation
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 600,
                              fontSize: 16,
                            }}
                          >
                            Penalty or Consequence
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>
                            <strong style={{ fontWeight: 600 }}>
                              Return filed late
                            </strong>
                          </TableCell>
                          <TableCell>
                            <strong style={{ fontWeight: 600 }}>
                              5% of unpaid tax
                            </strong>{' '}
                            for each month or part of a month the return is
                            late, up to{' '}
                            <strong style={{ fontWeight: 600 }}>
                              25%
                            </strong>{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>
                            <strong style={{ fontWeight: 600 }}>
                              Return more than 60 days late
                            </strong>
                          </TableCell>
                          <TableCell>
                            Minimum penalty is the
                            <strong style={{ fontWeight: 600 }}>
                              {' '}
                              smaller of the tax due or $525
                            </strong>{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>
                            <strong style={{ fontWeight: 600 }}>
                              Tax paid late
                            </strong>
                          </TableCell>
                          <TableCell>
                            <strong style={{ fontWeight: 600 }}>
                              {' '}
                              0.5% of unpaid tax
                            </strong>{' '}
                            for each month or part of a month, up to{' '}
                            <strong style={{ fontWeight: 600 }}>
                              {' '}
                              25%
                            </strong>{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>
                            <strong style={{ fontWeight: 600 }}>
                              Tax remains unpaid
                            </strong>
                          </TableCell>
                          <TableCell>
                            {' '}
                            <strong style={{ fontWeight: 600 }}>
                              {' '}
                              Interest
                            </strong>{' '}
                            applies until payment.{' '}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The late-filing penalty is based on the{' '}
                  <strong style={{ fontWeight: 600 }}>unpaid tax</strong>, so an
                  organization with no tax due may have a different penalty
                  outcome. Reasonable-cause relief may also apply when the
                  organization can demonstrate that the failure to file or pay
                  on time was due to reasonable cause.
                </Typography>
                <Alert
                  severity="info"
                  icon={<TipsAndUpdatesOutlinedIcon />}
                  sx={{ mb: 2, mt: 0, borderLeft: '3px solid #0288d1' }}
                >
                  <strong style={{ fontWeight: 600 }}> Tips : </strong> If you
                  know you cannot complete the return by the original deadline,{' '}
                  <Link
                    href="/tax-extension/efile-irs-form-7004-business-tax-extension-online/"
                    sx={{
                      color: '#0202ff',
                      textDecoration: 'underline',
                    }}
                  >
                    file Form 7004
                  </Link>{' '}
                  <strong style={{ fontWeight: 600 }}>on time</strong> and pay
                  any tax due by the original deadline rather than waiting until
                  the extended filing date.
                </Alert>
                <Typography
                  variant="h2"
                  id="filing"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                >
                  2026 filing deadline
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  For calendar-year organizations{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-1120-pol/"
                    sx={{
                      color: '#0202ff',
                      textDecoration: 'underline',
                    }}
                  >
                    filing Form 1120-POL
                  </Link>{' '}
                  for tax year 2025, the filing deadline is{' '}
                  <strong style={{ fontWeight: 600 }}> April 15</strong>, 2026.
                  An organization that timely files{' '}
                  <strong style={{ fontWeight: 600 }}> Form 7004 </strong> can
                  receive an automatic 6-month extension to{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    October 15, 2026{' '}
                  </strong>
                  .
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 600,
                              fontSize: 16,
                            }}
                          >
                            2026 Filing Deadline
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 600,
                              fontSize: 16,
                            }}
                          >
                            Extended Filing Deadline
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>
                            <strong style={{ fontWeight: 600 }}>
                              April 15, 2026
                            </strong>
                          </TableCell>
                          <TableCell>
                            <strong style={{ fontWeight: 600 }}>
                              October 15, 2026
                            </strong>{' '}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h2"
                  id="form1120polvs"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                >
                  Form 1120-POL vs. Form 990: Are the Due Dates the Same?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong style={{ fontWeight: 600 }}>
                    Form 1120-POL and{' '}
                  </strong>{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990/"
                    sx={{
                      color: '#0202ff',
                      textDecoration: 'underline',
                    }}
                  >
                    Form 990
                  </Link>{' '}
                  have different filing deadlines. A political organization
                  should determine which returns it is required to file rather
                  than assuming that the deadline for one return applies to the
                  other.
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 600,
                              fontSize: 16,
                            }}
                          >
                            Return
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 600,
                              fontSize: 16,
                            }}
                          >
                            General Due Date
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>
                            <strong style={{ fontWeight: 600 }}>
                              Form 1120-POL
                            </strong>
                          </TableCell>
                          <TableCell>
                            15th day of the 4th month after the tax year ends
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>
                            <strong style={{ fontWeight: 600 }}>
                              Form 990 / 990-EZ
                            </strong>
                          </TableCell>
                          <TableCell>
                            15th day of the 5th month after the tax year ends
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For a calendar-year organization, this means{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Form 1120-POL is due April 15
                  </strong>
                  , while Form 990 or{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-ez/"
                    sx={{
                      color: '#0202ff',
                      textDecoration: 'underline',
                    }}
                  >
                    990-EZ
                  </Link>{' '}
                  is generally due{' '}
                  <strong style={{ fontWeight: 600 }}>May 15</strong>. The
                  filing requirements depend on the organization's activities,
                  tax status, and applicable IRS rules.
                </Typography>
                <Alert
                  severity="info"
                  icon={<InfoIcon sx={{ color: '#640378' }} />}
                  sx={{
                    mb: 2,
                    mt: 0,
                    borderLeft: '3px solid #640378',
                    backgroundColor: '#f7e1ff',
                    color: '#444',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}> Important:</strong>{' '}
                  Filing one return does not automatically satisfy a separate
                  filing requirement. If your organization may have both Form
                  1120-POL and Form 990 obligations, review the applicable
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    Form 1120-POL filing requirements{' '}
                  </strong>
                  and IRS instructions for each return. 
                </Alert>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Source & Reference
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information on this page is based on the{' '}
                        <strong style={{ fontWeight: 600 }}>
                          IRS Form 1120-POL
                        </strong>{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i1120pol.pdf"
                          target="_blank"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Instructions
                        </Link>
                        , Form 7004{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i7004.pdf"
                          target="_blank"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          {' '}
                          Instructions
                        </Link>
                        ,{' '}
                        <strong style={{ fontWeight: 600 }}>
                          2026 Form 7004
                        </strong>{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-wi/Form%207004%20Due%20Date%20Chart%20PY2026%20dated%2011032025.pdf"
                          target="_blank"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Due-Date Chart,
                        </Link>{' '}
                        and{' '}
                        <Link
                          href="https://www.irs.gov/publications/p557"
                          target="_blank"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Publication 557
                        </Link>{' '}
                        .
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              File Form 1120-POL on Time with TaxZerone
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              <strong style={{ fontWeight: 600 }}>
                Know your deadline. Now, make filing easier
              </strong>
              . TaxZerone helps you prepare and e-file Form 1120-POL online with
              a guided filing experience, so you can submit your return
              confidently and stay on track with your filing deadline.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1120polduedates`}
              >
                File Form 1120-POL Online
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1120polDueDates;
